import React, { useMemo } from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import { DRAWER_WIDTH, ROUTES } from '@/lib/constants';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router';
import {
    BlurredContainer,
    StyledList,
} from '@/components/Hypertracker/HypertrackerSidebar/HypertrackerSidebarStyled';
import { Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import SidebarLogo from '@/components/Hypertracker/HypertrackerSidebar/SidebarLogo';
import ConnectionsList from '@/components/Hypertracker/HypertrackerSidebar/ConnectionsList';
import CohortsMenu from '@/components/Hypertracker/HypertrackerSidebar/CohortsMenu';
import SidebarItem from '@/components/Hypertracker/HypertrackerSidebar/SidebarItem';
import { Box } from '@mui/system';
import { CLOSED_DRAWER_WIDTH } from '@/lib/constants/generalConstants';
import AccountType from '@/components/Hypertracker/AsideMenu/AccountType';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectTelegramConnectionHT } from '@/state/hypertracker/hypertrackerSelector';
import { useQueryFetcher } from '@/lib/query/useQueryFetcher';
import { AlertsStatsResponse } from '@/lib/types/hypertrackerTypes';
import { nFormatter } from '@/lib/helpers/helpers';
import { HT_API_ROUTES } from '@/lib/http/hypertrackerHttpRequest';
import useConsumerDataQuery from '@/pages/HypertrackerApiDashboard/useConsumerDataQuery';
import AlphafeedItem from './AlphafeedItem';

const drawerWidth = DRAWER_WIDTH;
const miniDrawerWidth = CLOSED_DRAWER_WIDTH;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.07) 100%), #000',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    willChange: 'width',
});

const closedMixin = (theme: Theme): CSSObject => ({
    width: miniDrawerWidth,
    background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.07) 100%), #000',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    willChange: 'width',
});

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

const HypertrackerSidebar = () => {
    const isMemberAuthorized = useIsMemberAuthorized();
    const location = useLocation();
    const theme = useTheme();
    const { t } = useTranslation('translation', { keyPrefix: 'hypertracker_sidebar' });
    const [open, setOpen] = React.useState(false);
    const handleMouseEnter = () => setOpen(true);
    const handleMouseLeave = () => setOpen(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const telegramAccountData = useSelector((state: RootState) =>
        selectTelegramConnectionHT(state),
    );
    const alertsStatsQuery = useQueryFetcher<AlertsStatsResponse>({
        mode: 'api',
        apiPath: HT_API_ROUTES.ALERTS.STATS,
        isAuthorized: isMemberAuthorized && !!telegramAccountData,
    });
    const totalActiveAlertsFormatted = useMemo(
        () => nFormatter(alertsStatsQuery?.data?.totalActiveAlerts || 0),
        [alertsStatsQuery.data],
    );
    const { consumer } = useConsumerDataQuery();
    const hasApiToken = isMemberAuthorized && !!consumer?.accessToken;

    const mainGroup = useMemo(() => {
        const fullList = [
            {
                label: 'menu_alphafeed',
                url: ROUTES.HYPERTRACKER_ALPHAFEED,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'trade',
            },
            {
                label: 'menu_home',
                url: ROUTES.HYPERTRACKER,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'main',
            },
            {
                label: 'menu_perps',
                url: ROUTES.HYPERTRACKER_PERPS,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'market_analytics',
            },
            {
                label: 'menu_radar',
                url: ROUTES.HYPERTRACKER_MARKET_RADAR,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'market_analytics',
            },
            {
                label: 'menu_cohorts',
                url: '/hypertracker/segments/:id',
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'market_analytics',
            },
            {
                label: 'menu_heatmap',
                url: ROUTES.HYPERTRACKER_HEATMAP,
                subMenu: [],
                authorized: false,
                visible: true,
                group: 'market_analytics',
            },
            {
                label: 'menu_liquidations',
                url: ROUTES.HYPERTRACKER_LIQUIDATIONS,
                subMenu: [],
                authorized: false,
                visible: true,
                group: 'market_analytics',
            },
            {
                label: 'menu_flows',
                url: ROUTES.HYPERTRACKER_FLOWS,
                subMenu: [],
                authorized: false,
                visible: false,
                group: 'market_analytics',
            },
            {
                label: 'menu_builders',
                url: ROUTES.HYPERTRACKER_BUILDER,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'overview',
            },
            {
                label: 'menu_leaderboard',
                url: ROUTES.HYPERTRACKER_LEADERBOARDS,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'overview',
            },
            {
                label: 'menu_hip3',
                url: ROUTES.HYPERTRACKER_HIP,
                subMenu: [],
                visible: false,
                authorized: false,
                group: 'overview',
            },

            {
                label: 'menu_stats',
                url: ROUTES.HYPERTRACKER_STATS,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'overview',
            },
            {
                label: 'menu_favorites',
                url: ROUTES.HYPERTRACKER_FAVORITES,
                subMenu: [],
                visible: true,
                authorized: true,
                group: 'tools',
            },
            {
                label: 'menu_alerts',
                url: ROUTES.HYPERTRACKER_ALERTS,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'tools',
            },
            {
                label: 'menu_cmm_dashboard',
                url: ROUTES.HOME,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'tools',
            },
            {
                id: 'menu_api',
                label: hasApiToken ? 'menu_api_dashboard' : 'menu_api_new',
                url: hasApiToken ? ROUTES.HYPERTRACKER_API_DASHBOARD : ROUTES.HYPERTRACKER_API,
                subMenu: [],
                visible: true,
                authorized: false,
                group: 'api',
            },
        ];

        return fullList.filter((item) => item.visible);
    }, [hasApiToken]);

    const defineIsSelected = (url: string | null) => {
        if (!url) return false;
        const isDynamic = url.includes(':');

        return Boolean(matchPath({ path: url, end: !isDynamic }, location.pathname));
    };

    if (isMobile) return null;

    return (
        <>
            <Drawer
                data-testid="hypertracker-sidebar"
                variant="permanent"
                open={open}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{
                    '& .MuiDrawer-paper': {
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                    },

                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <SidebarLogo open={open} />
                {isMemberAuthorized && <AccountType isOpen={open} />}

                <StyledList
                    sx={{
                        flexGrow: 1,
                        overflowY: 'scroll',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    <Divider orientation="horizontal" />
                    {mainGroup.map((menuItem) => {
                        const isSelected = defineIsSelected(menuItem.url);

                        if (menuItem.label === 'menu_alphafeed') {
                            return (
                                <AlphafeedItem
                                    open={open}
                                    key={menuItem.label}
                                    isSelected={isSelected}
                                    t={t}
                                />
                            );
                        }

                        if (menuItem.label === 'menu_cohorts') {
                            return (
                                <CohortsMenu
                                    key={menuItem.label}
                                    menuItem={menuItem}
                                    open={open}
                                    isSelected={isSelected}
                                    setOpen={setOpen}
                                />
                            );
                        }

                        return (
                            <React.Fragment key={menuItem.id ?? menuItem.label}>
                                {menuItem.label === 'menu_home' && (
                                    <Typography
                                        variant={'p12'}
                                        color={theme.palette.primitives.woodsmoke[700]}
                                        textAlign={open ? 'unset' : 'center'}
                                        component={'p'}
                                        width={'100%'}
                                        sx={{
                                            padding: '0 18px',
                                            margin: '16px 0 4px',
                                        }}
                                    >
                                        {t(
                                            !open
                                                ? 'group_market_analytics_short'
                                                : 'group_market_analytics',
                                        )}
                                    </Typography>
                                )}
                                {menuItem.label === 'menu_builders' && (
                                    // <Typography
                                    //     variant={'p12'}
                                    //     color={'customText.text1'}
                                    //     textAlign={open ? 'unset' : 'center'}
                                    //     component={'p'}
                                    //     width={'100%'}
                                    //     sx={{
                                    //         padding: '0 18px',
                                    //         margin: '16px 0 4px',
                                    //     }}
                                    // >
                                    //     {t(!open ? 'group_overview_short' : 'group_overview')}
                                    // </Typography>
                                    <Box sx={{ padding: '0 8px', margin: '8px 0' }}>
                                        <Divider orientation="horizontal" />
                                    </Box>
                                )}

                                {menuItem.label === 'menu_favorites' && (
                                    <Typography
                                        variant={'p12'}
                                        color={theme.palette.primitives.woodsmoke[700]}
                                        textAlign={open ? 'unset' : 'center'}
                                        component={'p'}
                                        width={'100%'}
                                        sx={{
                                            padding: '0 18px',
                                            margin: '16px 0 4px',
                                        }}
                                    >
                                        {t(!open ? 'group_tools_short' : 'group_tools')}
                                    </Typography>
                                )}

                                <SidebarItem
                                    key={menuItem.id ?? menuItem.label}
                                    t={t}
                                    menuItem={menuItem}
                                    open={open}
                                    isSelected={isSelected}
                                    totalActiveAlertsFormatted={totalActiveAlertsFormatted}
                                />
                            </React.Fragment>
                        );
                    })}
                </StyledList>
                <Box
                    data-testid="hypertracker-sidebar-connections-list"
                    sx={{
                        minHeight: '65px',
                    }}
                >
                    <ConnectionsList open={open} setOpen={setOpen} />
                </Box>
            </Drawer>
            <BlurredContainer
                sx={{
                    opacity: open ? 1 : 0,
                    pointerEvents: open ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                }}
                onClick={() => setOpen(false)}
            />
        </>
    );
};

export default HypertrackerSidebar;
